import React, { useEffect, useRef, useState } from 'react';
// MUST stay a bare specifier: ozira-app-core is CommonJS, and only the
// consuming app's Vite alias + optimizeDeps pre-bundling gives it working
// CJS->ESM interop in the browser. A relative path to the .js bypasses both
// and ships raw `require(...)` to the page. Types come from
// src/oziraAppCore.d.ts in this package.
import * as OziraAppCore from 'ozira-app-core';
import { buildProductHandoffUrl, ProductHandoffContext } from './productHandoff';

// Product switcher (Figma 66:392). Switching products is a real cross-origin
// navigation to that product's own domain -- the target re-authenticates and
// re-authorizes independently; this component only ever forwards the
// allowlisted, non-secret context via buildProductHandoffUrl (spec: "Product
// switch preserves context, not authority").
export interface OziraProductSwitcherProps {
  currentProductId: string;
  productUrls: Record<string, string>;
  context: ProductHandoffContext;
  onNavigate?: (url: string, productId: string) => void; // testing hook; defaults to window.location
}

export function OziraProductSwitcher({ currentProductId, productUrls, context, onNavigate }: OziraProductSwitcherProps) {
  const manifests = OziraAppCore.listManifests();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLElement>(null);
  const current = manifests.find((manifest: { productId: string }) => manifest.productId === currentProductId);
  const alternatives = manifests.filter((manifest: { productId: string }) => manifest.productId !== currentProductId);
  const alternativesId = `ozira-product-alternatives-${currentProductId.replace(/[^a-z0-9-]/gi, '-')}`;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        rootRef.current?.querySelector<HTMLButtonElement>('.ozira-product-switcher__current')?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function destination(productId: string) {
    const base = productUrls[productId];
    return base ? buildProductHandoffUrl(base, context) : null;
  }

  return (
    <nav className="ozira-product-switcher" aria-label="Switch product" ref={rootRef}>
      <button
        type="button"
        className="ozira-product-switcher__item ozira-product-switcher__current is-current"
        aria-current="page"
        aria-controls={alternativesId}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {current?.displayName ?? currentProductId}
        <span aria-hidden="true" className="ozira-product-switcher__chevron">⌄</span>
      </button>
      <div
        id={alternativesId}
        className={`ozira-product-switcher__menu${open ? ' is-open' : ''}`}
        hidden={!open}
      >
        {alternatives.map((manifest: { productId: string; displayName: string }) => {
        const href = destination(manifest.productId);
        if (!href) {
          return (
            <span key={manifest.productId} className="ozira-product-switcher__item" aria-disabled="true">
              {manifest.displayName}
            </span>
          );
        }
        return (
          <a
            key={manifest.productId}
            className="ozira-product-switcher__item"
            href={href}
            onClick={(event) => {
              if (onNavigate) {
                event.preventDefault();
                setOpen(false);
                onNavigate(href, manifest.productId);
              } else {
                setOpen(false);
              }
            }}
          >
            {manifest.displayName}
          </a>
        );
        })}
      </div>
    </nav>
  );
}
